import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { GenericHeightWeightQuestionFieldAnswers } from '../../Generic/HeightWeight/question';

const sermorelinQuestions = [
  {
    field: 'sermorelin_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Male',
          key: 'male'
        },
        {
          value: 'Female',
          key: 'female'
        }
      ]
    }
  },
  {
    field: 'sermorelin_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'sermorelin_pregnancyConsent',
    label: 'Consent (Pregnancy Risk):',
    type: 'select',
    educationalSlide:
      'I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.\n\nI agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.\n\nI understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.\n\nBy selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'sermorelin_height',
    label: 'What is your height in feet and inches?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'sermorelin_height'
    }
  },
  {
    field: 'sermorelin_weight',
    label: 'What is your weight in pounds?',
    type: 'info',
    answer: {
      type: 'number',
      name: 'sermorelin_weight'
    }
  },
  {
    field: 'sermorelin_goals',
    label: 'We would like to know your goals:',
    type: 'select',
    answer: {
      type: 'multi_select',
      name: 'sermorelin_goals',
      options: [
        {
          value: 'Maintain a healthy weight',
          key: 'maintain_weight'
        },
        {
          value: 'Improve metabolic health',
          key: 'metabolic_health'
        },
        {
          value: 'Improve cardiovascular health',
          key: 'cardiovascular_health'
        },
        {
          value: 'Improve overall quality of life',
          key: 'quality_of_life'
        },
        {
          value: 'Improve mood',
          key: 'improve_mood'
        },
        {
          value: 'Increase energy',
          key: 'increase_energy'
        },
        {
          value: 'Improve muscle tone',
          key: 'muscle_tone'
        },
        {
          value: 'Other',
          key: 'other'
        }
      ]
    }
  },
  {
    field: 'sermorelin_customGoal',
    label: 'Tell us about your goal with therapy',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'sermorelin_customGoal'
    }
  },
  {
    field: 'sermorelin_cardiovascularHealth',
    label:
      'On a scale of 1-5, how would you rate your average cardiovascular health?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: '1 (poor)',
          key: '1'
        },
        {
          value: '2',
          key: '2'
        },
        {
          value: '3',
          key: '3'
        },
        {
          value: '4',
          key: '4'
        },
        {
          value: '5 (excellent)',
          key: '5'
        }
      ]
    }
  },
  {
    field: 'sermorelin_strength',
    label: 'On a scale of 1-5, how would you rate your physical strength?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: '1 (poor)',
          key: '1'
        },
        {
          value: '2',
          key: '2'
        },
        {
          value: '3',
          key: '3'
        },
        {
          value: '4',
          key: '4'
        },
        {
          value: '5 (excellent)',
          key: '5'
        }
      ]
    }
  },
  {
    field: 'sermorelin_medicalConditions',
    label:
      'Do you currently have, or have you ever been diagnosed with, any of the following conditions?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'None of these apply to me',
          key: 'none',
          uncheckOthers: true
        },
        {
          value: 'Any active or history of cancer',
          key: 'cancer'
        },
        {
          value: 'Hypothyroidism/Underactive Thyroid',
          key: 'hypothyroidism'
        }
      ]
    }
  },
  {
    field: 'sermorelin_thyroidControl',
    label: 'Is your thyroid disorder well controlled?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'sermorelin_thyroidControl'
    }
  },
  {
    field: 'sermorelin_thyroidControlConsent',
    type: 'select',
    label:
      'Sermorelin and tesamorelin can affect thyroid function by influencing how the body produces and converts thyroid hormones. If you have hypothyroidism or take thyroid medication, these treatments may alter your hormone levels or require adjustments to your thyroid dose. Monitoring thyroid function during therapy is recommended. Please report any changes in energy, mood, or temperature tolerance to your provider, as these may indicate a change in thyroid balance.',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the consent and I do NOT wish to move forward',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I wish to proceed and will discuss this medication with my doctor',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },

  //! This question is not being used, it is here for the backward compatibility
  {
    field: 'sermorelin_currentMedications',
    label: 'Do you take any of the following medications?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'COX inhibitors (Aspirin, indomethacin)',
          key: 'cox_inhibitors'
        },
        {
          value: 'Muscarinic antagonist (atropine)',
          key: 'muscarinic_antagonist'
        },
        {
          value: 'Somatostatin-containing medications (clonidine and levodopa)',
          key: 'somatostatin'
        },
        {
          value: 'Propylthiouracil',
          key: 'propylthiouracil'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'sermorelin_currentMedications_new',
    label: 'Do you take any of the following medications?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Muscarinic antagonist (atropine)',
          key: 'muscarinic_antagonist'
        },
        {
          value: 'Somatostatin-containing medications (clonidine and levodopa)',
          key: 'somatostatin'
        },
        {
          value: 'Propylthiouracil',
          key: 'propylthiouracil'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'sermorelin_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'sermorelin_additionalQuestions'
    }
  },
  {
    field: 'sermorelin_consent',
    label: 'Consent:',
    type: 'select',
    educationalSlide: `Medications such as sermorelin and tesamorelin are medications that may be prescribed to support your metabolism, though it is not FDA-approved for this specific use. Its application for metabolism support is considered off-label, meaning it is prescribed by your healthcare provider despite not being specifically approved by the FDA for this purpose. The purpose of this consent form is to ensure that you understand the following:
- The reasons for using sermorelin or tesamorelin
- The potential benefits and risks
- The monitoring plan
- Your responsibilities during treatment

Sermorelin and tesamorelin are being considered as a treatment option to potentially improve your metabolic support by acting as a synthetic analogue of growth hormone-releasing hormone (GHRH), which stimulates the secretion of growth hormone (GH). Growth hormone plays a critical role in:
- Growth
- Body composition
- Cell repair
- Metabolism

By promoting the natural production of GH, these medications may provide the following benefits:
- Enhance muscle growth
- Improve body composition
- Boost energy levels
- Support overall physical performance

It is commonly used for purposes like weight loss, muscle gain, and anti-aging therapy.

As previously mentioned, the use of sermorelin and tesamorelin to increase growth hormone levels is considered off-label. While legally prescribed, it is not an FDA-approved indication.

Like any medication, these comes with potential risks and side effects:
Common side effects:
- Injection site reactions (pain, swelling, redness) if provided as an injection
- Headaches
- Flushing
- Dizziness
- Nausea

Less common but more serious risks:
- Exacerbation of undiagnosed cancers
- Significant changes in blood pressure
- Increased intracranial pressure
- Severe allergic reactions

There are also concerns regarding the long-term use and its potential link to increased cancer risk. While there is no definitive evidence directly linking these to cancer, some studies suggest that prolonged use of growth hormone therapies could potentially increase the risk of certain cancers due to hormonal imbalances. Since these products stimulate the production of growth hormone, it may influence the development of cancerous cells in susceptible individuals.

To ensure safety and effectiveness, regular monitoring is required during treatment including:
- Blood tests every six months to monitor growth hormone levels
- Regular symptom assessments to evaluate the medication’s effectiveness
- Follow-up visits to discuss progress, side effects, and necessary adjustments to the treatment plan

You have the right to discuss and consider alternative treatments with your healthcare provider. These alternatives may include:
- Lifestyle modifications (exercise, diet, sleep hygiene, stress management)

As a patient, you are responsible for:
- Attending all scheduled follow-up appointments
- Reporting any side effects or new symptoms to your healthcare provider
- Adhering to the prescribed dosing regimen
- Undergoing all required laboratory tests as part of the monitoring plan if needed

By selecting below, you confirm that you:
- Have read and understood the information provided
- Understand the potential risks and benefits of using sermorelin or tesamorelin, as well as its off-label nature
- Agree to adhere to the monitoring plan and report any concerns or side effects to your healthcare provider
`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the consent and I do NOT wish to move forward',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the consent and understand the content, I wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'sermorelin_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'sermorelin_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    type: 'info',
    reject: true,
    isLast: true
  },
  {
    class: 'ineligible',
    field: 'sermorelin_didNotConsent',
    label: 'We need your consent to continue',
    subLabel:
      'You did not agree to the terms shown on the previous step. Without that consent, we cannot move forward with your visit or prescribe treatment.',
    extras: { sendToBeluga: false },
    reject: true,
    answer: {},
    type: 'info',
    isLast: true
  }
] as const;

const _type = sermorelinQuestions;
export type SermorelinFieldAnswers = Partial<QuestionAnswerMap<typeof _type>> &
  GenericHeightWeightQuestionFieldAnswers;
export default sermorelinQuestions;
